import {
  listManufacturingRecipeAttachments,
  setManufacturingRecipeAttachments,
} from "@/lib/manufacturing/recipesClient";
import type { MfgRecipeAttachment } from "@/lib/manufacturing/types/recipes";

export type AttachPackageResult =
  | { parent_recipe_id: string; ok: true; skipped: boolean }
  | { parent_recipe_id: string; ok: false; error: string };

/** Pure merge: append package if missing; preserve existing attachment fields. */
export function mergePackageOntoAttachments(
  existing: readonly MfgRecipeAttachment[],
  packageRecipeId: string,
): { attachments: Array<{
  attached_recipe_id: string;
  included_by_default?: boolean;
  customer_price_delta?: number | null;
  replaces_slot_labels?: string[];
  quantity?: number;
  sort_order?: number;
  notes?: string;
}>; alreadyPresent: boolean } {
  const alreadyPresent = existing.some((a) => a.attached_recipe_id === packageRecipeId);
  const attachments: Array<{
    attached_recipe_id: string;
    included_by_default?: boolean;
    customer_price_delta?: number | null;
    replaces_slot_labels?: string[];
    quantity?: number;
    sort_order?: number;
    notes?: string;
  }> = existing.map((a) => ({
    attached_recipe_id: a.attached_recipe_id,
    included_by_default: a.included_by_default,
    customer_price_delta: a.customer_price_delta,
    replaces_slot_labels: a.replaces_slot_labels,
    quantity: a.quantity,
    sort_order: a.sort_order,
    notes: a.notes,
  }));
  if (!alreadyPresent) {
    attachments.push({
      attached_recipe_id: packageRecipeId,
      included_by_default: false,
      sort_order: attachments.length,
    });
  }
  return { attachments, alreadyPresent };
}

/** Attach one package recipe to many parents without wiping sibling attachments. */
export async function attachPackageToRecipes(
  parentRecipeIds: readonly string[],
  packageRecipeId: string,
): Promise<AttachPackageResult[]> {
  const pkg = packageRecipeId.trim();
  if (!pkg) return [];
  const results: AttachPackageResult[] = [];
  for (const parent_recipe_id of parentRecipeIds) {
    const listed = await listManufacturingRecipeAttachments(parent_recipe_id);
    if (!listed.ok) {
      results.push({ parent_recipe_id, ok: false, error: listed.error });
      continue;
    }
    const merged = mergePackageOntoAttachments(listed.data.attachments, pkg);
    if (merged.alreadyPresent) {
      results.push({ parent_recipe_id, ok: true, skipped: true });
      continue;
    }
    const set = await setManufacturingRecipeAttachments({
      parent_recipe_id,
      attachments: merged.attachments,
    });
    if (!set.ok) {
      results.push({ parent_recipe_id, ok: false, error: set.error });
      continue;
    }
    results.push({ parent_recipe_id, ok: true, skipped: false });
  }
  return results;
}
